import React, { useRef, useState, useCallback } from 'react'
import { PDFDocument } from 'pdf-lib'

import { Icon } from '@iconify/react'
import { showErrorToast } from '../../utils/showToast'
import CornerConfirmModal from '../ui/CornerConfirmModal'
import { MODE } from '../../data/queryFactories'

const FileInput = ({
  mode,
  acceptedFileTypes,
  acceptedImageType,
  file,
  fileInfo,
  onFileChange,
  onRemoveFile,
  image,
  onImageChange,
  onImageRemove,
}) => {
  const [pdfKey, setPdfKey] = useState(0)
  const [imageKey, setImageKey] = useState(0)
  const fileInputRef = useRef(null)
  const imageInputRef = useRef(null)
  const [isProcessingPdf, setIsProcessingPdf] = useState(false)
  const [isProcessingImage, setIsProcessingImage] = useState(false)
  const [showImageRemoveConfirm, setShowImageRemoveConfirm] = useState(false)

  const imagePreviewStyling =
    'w-[12.5vw] h-[36vh] border-2 border-dashed border-secondarygray rounded-xl flex flex-col justify-center items-center  bg-white'
  // 'w-52 h-80 border-dashed border-2 border-gray-500 rounded-lg flex justify-center flex-col items-center'

  const processPdfFile = useCallback(
    async (selectedFile) => {
      setIsProcessingPdf(true)
      try {
        if (selectedFile.type === 'application/pdf') {
          const arrayBuffer = await selectedFile.arrayBuffer()
          const pdfDoc = await PDFDocument.load(arrayBuffer, {
            ignoreEncryption: true,
          })
          const info = {
            pages: pdfDoc.getPageCount(),
            // size: (selectedFile.size / 1024 / 1024).toFixed(2) + ' ' + 'MB',
            size: (selectedFile.size / 1024 / 1024).toFixed(2),
          }
          onFileChange(selectedFile, info)
        } else {
          onFileChange(selectedFile, null)
        }
      } catch (error) {
        showErrorToast({
          message: 'Có lỗi xảy ra khi xử lý file',
          error: 'Không thể xử lí file',
        })
        onRemoveFile()
      } finally {
        setIsProcessingPdf(false)
      }
    },
    [onFileChange, onRemoveFile]
  )

  const handlePdfChange = useCallback(
    (event) => {
      const selectedFile = event.target.files[0]
      if (!selectedFile) {
        return showErrorToast({
          message: 'Hãy chọn file',
          error: 'Chưa có file được chọn',
        })
      }

      const isAcceptedType = acceptedFileTypes.includes(selectedFile.type)

      if (!isAcceptedType) {
        return showErrorToast({
          message: 'Sai định dạng được cho phép',
          error: '',
        })
      }

      processPdfFile(selectedFile)
    },
    [acceptedFileTypes, processPdfFile]
  )

  const handleImageChange = useCallback(
    (event) => {
      const selectedFile = event.target.files[0]
      if (!selectedFile) {
        return showErrorToast({
          message: 'Hãy chọn file',
          error: 'Chưa có file được chọn',
        })
      }

      const isAcceptedType = selectedFile.type.includes('image')

      if (!isAcceptedType) {
        return showErrorToast({
          message: 'Sai định dạng được cho phép',
          error: '',
        })
      }

      setIsProcessingImage(true)
      try {
        onImageChange(selectedFile)
      } catch (error) {
        showErrorToast({
          message: 'Có lỗi xảy ra khi xử lý file ảnh',
          error: 'Không thể xử lí file',
        })
        onImageRemove()
      } finally {
        setIsProcessingImage(false)
      }
    },
    [onImageChange, onImageRemove]
  )

  const handleRemovePdf = useCallback(() => {
    onRemoveFile()
    setPdfKey((prevKey) => prevKey + 1)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [onRemoveFile])

  const handleRemoveImage = useCallback(() => {
    setShowImageRemoveConfirm(true)
  }, [])

  const confirmRemoveImage = useCallback(() => {
    onImageRemove()
    setImageKey((prevKey) => prevKey + 1)
    if (imageInputRef.current) {
      imageInputRef.current.value = ''
    }
    setShowImageRemoveConfirm(false)
  }, [onImageRemove])

  return (
    <section>
      <h3 className="text-base font-bold">
        File Sách <span className="text-buttonred">*</span>
      </h3>

      <div className="flex flex-col justify-center">
        {file ? (
          <>
            <p className="text-md my-4 ml-4 line-clamp-3 text-center text-black">
              {file.name}
            </p>
          </>
        ) : mode === MODE.EDIT ? null : (
          <>
            <p className="text-md my-4 ml-4 text-center text-gray-secondary">
              Chưa có tệp
            </p>
          </>
        )}

        <div className="mb-4 flex flex-col">
          <div className={imagePreviewStyling}>
            {isProcessingImage ? (
              <div className="flex flex-col items-center"></div>
            ) : image ? (
              <div className="group relative h-full w-full">
                <img
                  src={URL.createObjectURL(image)}
                  alt="Preview Cover"
                  className="h-full w-full rounded-lg object-cover"
                />
                <button
                  className="absolute bottom-4 left-1/2 flex -translate-x-1/2 cursor-pointer items-center justify-center rounded bg-red-600 px-4 py-2 font-bold text-white opacity-0 transition-opacity group-hover:opacity-100"
                  onClick={handleRemoveImage}
                  disabled={isProcessingPdf || mode === MODE.EDIT}
                >
                  <Icon
                    icon="ph:x-bold"
                    width="24px"
                    height="24px"
                    className="flex-shrink-0"
                    style={{ color: '#ffffff' }}
                  />
                  <span className="ml-1 whitespace-nowrap">Gỡ ảnh bìa</span>
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center">
                <Icon
                  icon="ph:file-dashed-bold"
                  width="32px"
                  height="32px"
                  style={{ color: '#303030' }}
                />
              </div>
            )}
          </div>
          {fileInfo ? (
            <div className="mt-4">
              <div>
                <span className="font-bold">Dung lượng:</span> {fileInfo.size} MB
              </div>
              <div>
                <span className="font-bold">Số trang: </span>
                {fileInfo.pages}
              </div>
            </div>
          ) : (
            <div className="mt-4">
              <div>
                <span className="font-bold">Dung lượng:</span> --
              </div>
              <div>
                <span className="font-bold">Số trang: </span> --
              </div>
            </div>
          )}

          <div className="mx-2 mt-4 flex w-full flex-col gap-3">
            <label
              className={`flex w-full cursor-pointer items-center justify-center rounded bg-accentgray px-[5.83vw] py-2 font-bold text-white hover:bg-opacity-30 ${mode === MODE.EDIT ? 'bg-opacity-30' : ''}`}
            >
              <Icon
                icon="ph:file-plus-bold"
                width="24px"
                height="24px"
                className="flex-shrink-0"
                style={{
                  color: 'white',
                  opacity: mode === MODE.EDIT ? 0.5 : 1,
                }}
              />
              <span
                className={`ml-1 whitespace-nowrap ${mode === MODE.EDIT ? 'text-opacity-30' : ''}`}
              >
                Tải file PDF
              </span>
              <input
                key={pdfKey}
                ref={fileInputRef}
                type="file"
                onChange={handlePdfChange}
                accept={acceptedFileTypes}
                className="hidden"
                disabled={isProcessingPdf || mode === MODE.EDIT}
              />
            </label>

            <label
              className={`flex w-full cursor-pointer items-center justify-center rounded bg-accentgray px-[5.83vw] py-2 font-bold text-white hover:bg-opacity-30 ${mode === MODE.EDIT ? 'bg-opacity-30' : ''}`}
            >
              <Icon
                icon="ph:upload-simple"
                width="24px"
                height="24px"
                className="flex-shrink-0"
                style={{ color: 'white' }}
              />
              <span
                className={`ml-1 whitespace-nowrap ${mode === MODE.EDIT ? 'text-opacity-30' : ''}`}
              >
                Đổi ảnh bìa
              </span>
              <input
                key={imageKey}
                ref={imageInputRef}
                type="file"
                onChange={handleImageChange}
                accept={acceptedImageType || 'image/*'}
                className="hidden"
                disabled={isProcessingImage || mode === MODE.EDIT}
              />
            </label>

            {file ? (
          <>
            <button
              className={`flex w-full cursor-pointer items-center justify-center truncate rounded border border-red-accent bg-white px-[5.83vw] py-2 font-bold text-red-accent hover:bg-opacity-30 ${mode === MODE.EDIT ? 'border-opacity-30 text-opacity-30' : ''}`}
              onClick={handleRemovePdf}
              disabled={isProcessingPdf || !file || mode === MODE.EDIT}
            >
              <Icon
                icon="ph:x-bold"
                width="24px"
                height="24px"
                className="flex-shrink-0"
                style={{
                  color: '#EC3900',
                  opacity: mode === MODE.EDIT ? 0.5 : 1,
                }}
              />
              <span
                className={`ml-1 whitespace-nowrap ${mode === MODE.EDIT ? 'text-opacity-30' : ''}`}
              >
                Gỡ file sách
              </span>
            </button>
          </>
        ): null}
            
          </div>
        </div>
      </div>

      {showImageRemoveConfirm && (
        <CornerConfirmModal
          title="Xác nhận xóa ảnh bìa"
          content="Ảnh bìa hiện tại sẽ bị xóa và đổi về ảnh bìa mặc định"
          onSubmit={confirmRemoveImage}
          onClose={() => setShowImageRemoveConfirm(false)}
          icon="ph:warning-bold"
          iconColor="text-warning"
          titleColor="text-warning"
          confirmButtonVariant="warning"
        />
      )}
    </section>
  )
}

export default FileInput
